import {useFrame, useThree} from "@react-three/fiber";
import {Text} from "@react-three/drei";
import {useRef, useEffect, useCallback, useMemo} from "react";
import React from "react";
import * as THREE from "three";
import {Vec2, Vec3} from "../../../../../Utils/ThreeJS/VectorStructs.js";
import {MapState} from "../../../../../Store/main/maps/@MapState.js";
import {LabelPlacement} from "../../../../../Server/DB/mapObjects/@MapObject.js";
import {AssertUnreachable} from "web-vcore";
import {Assert} from "js-vextensions";
import {mapZIndexes} from "../MapZIndexes.js";
import {CreateRoundedRectMaterial, SetRoundedRectSize, SetRoundedRectRadius} from "./RoundedRectShader.js";
import {renderOrders} from "../../../../../Utils/UI/ZIndexes.js";

export type AdaptiveLabel_SizeInfo =
	| {width: number; minPixelWidth: number; minPixelHeight: number; maxPixelWidth: number; maxPixelHeight: number}
	| {height_for1Line: number; minPixelWidth: number; minPixelHeight: number; maxPixelWidth: number; maxPixelHeight: number};

export const calcMeterToPixelRatio = (camera: THREE.OrthographicCamera, canvasWidth: number)=>{
	const meterToPixelRatio_preZoom = canvasWidth / (camera.right - camera.left);
	return meterToPixelRatio_preZoom * camera.zoom;
};
export const useMeterToPixelRatio = (mapState: MapState|n, updateOnZoomChange = true)=>{
	const {camera: camera_base, size: canvasSize} = useThree();
	const camera = camera_base as THREE.OrthographicCamera;
	if (updateOnZoomChange) {
		Assert(mapState != null, "mapState must be provided if updateOnZoomChange is true");
		mapState.camZoom_0ms; // eslint-disable-line
	}
	return calcMeterToPixelRatio(camera, canvasSize.width);
};

function placementToAnchor(placement: LabelPlacement): {anchorX: "center"|"left"|"right"; anchorY: "middle"|"top"|"bottom"} {
	switch (placement) {
		case LabelPlacement.center: return {anchorX: "center", anchorY: "middle"};
		case LabelPlacement.left:   return {anchorX: "right",  anchorY: "middle"};
		case LabelPlacement.right:  return {anchorX: "left",   anchorY: "middle"};
		case LabelPlacement.top:    return {anchorX: "center", anchorY: "bottom"};
		case LabelPlacement.bottom: return {anchorX: "center", anchorY: "top"};
		default: return AssertUnreachable(placement);
	}
}

type TextRef = {
	fontSize: number;
	textRenderInfo?: {
		blockBounds: [number, number, number, number];
		visibleBounds: [number, number, number, number];
		// note: there are lots of other fields here too
	};
};

/** Text label rendered as a real 3D object (troika-three-text via @react-three/drei Text). */
export const AdaptiveLabel = (props: {mapState: MapState; position: Vec3; offset: Vec2; placement: LabelPlacement; sizeInfo: AdaptiveLabel_SizeInfo; text: string})=>{
	const {position, offset, placement, sizeInfo, text} = props;

	const textRef = useRef<TextRef>(null);
	const correctionGroupRef = useRef<THREE.Group>(null);
	const bgRef = useRef<THREE.Mesh>(null);
	const dotRef = useRef<THREE.Mesh>(null);

	const lineCount = text.split("\n").length;
	const {anchorX, anchorY} = placementToAnchor(placement);
	const finalPos = position.Plus(offset.Cast(Vec2).ToVec3(mapZIndexes.objLabel)).To3JPos();

	// Font size in world units — fixed for the lifetime of this render; never changed as a prop.
	// All sizing corrections are applied via correctionGroup scale/position instead.
	const initialFontSize = "height_for1Line" in sizeInfo ? sizeInfo.height_for1Line : sizeInfo.width * 0.4;

	// Natural geometry computed in onSync; consumed each frame by useFrame for pixel clamping.
	const naturalRef = useRef({naturalW: 0, naturalH: 0, paddedW: 0, paddedH: 0, rawScale: 1, cx: 0, cy: 0});

	// some constants
	const paddingPercent = 0.3; // padding as % of visual/actual line-height, applied to all sides
	const paddingMultiplier_vert = 0.5; // vert-padding should be less (accounting for block-bounds extending beyond actual top/bottom of text)
	const borderRadiusPercent = 0.3; // border radius as % of visual/actual line-height
	// weighting of block-bounds (rel to visible-bounds), for calc'ing text bounds
	const blockBoundsWeight_h = 0;
	const blockBoundsWeight_v = 0.3;

	// deps-array empty, because we call SetRoundedRectSize and SetRoundedRectRadius each frame anyway (in useFrame)
	// (also, the equation for calculating the radius is more naive here; it's fine since useFrame runs quickly anyway)
	const bgMaterial = useMemo(()=>CreateRoundedRectMaterial({radius: initialFontSize * borderRadiusPercent}), []); // eslint-disable-line

	// Called by troika after each layout pass — visibleBounds and fontSize are always in sync here.
	// We imperatively set scale/position on correctionGroup; these are safe from reconciler interference.
	const onSync = useCallback(()=>{
		const grp = correctionGroupRef.current;
		if (!textRef.current || !grp) return;
		const visibleBounds = textRef.current.textRenderInfo?.visibleBounds;
		const blockBounds = textRef.current.textRenderInfo?.blockBounds;
		if (!visibleBounds || !blockBounds) return;
		const [x0, y0, x1, y1] = visibleBounds.map((val_vis, i)=>{
			const blockBoundsWeightHere = i % 2 == 0 ? blockBoundsWeight_h : blockBoundsWeight_v;
			return (val_vis * (1 - blockBoundsWeightHere)) + (blockBounds[i] * blockBoundsWeightHere);
		}) as [number, number, number, number];
		const naturalW = x1 - x0;
		const naturalH = y1 - y0;
		if (naturalW <= 0 || naturalH <= 0) return;

		// Padding = X% of naive (unscaled) per-line height, on each side.
		const naivePadding = (naturalH / lineCount) * paddingPercent;
		const paddedW = naturalW + (naivePadding * 2);
		const paddedH = naturalH + (naivePadding * 2 * paddingMultiplier_vert);
		//const paddedH_if1Line = (naturalH / lineCount) + (naivePadding * 2 * paddingMultiplier_vert);

		// rawScale fits the text + padding to the target area.
		const rawScale = "width" in sizeInfo
			? sizeInfo.width / paddedW
			: (sizeInfo.height_for1Line * lineCount) / paddedH;

		const cx = (x0 + x1) / 2;
		const cy = (y0 + y1) / 2;

		naturalRef.current = {naturalW, naturalH, paddedW, paddedH, rawScale, cx, cy};
		// useFrame will apply the actual scale/position each frame (needed because pixel clamping depends on mtp)
	}, [lineCount, sizeInfo]);

	// Each frame: apply pixel min/max clamping on top of rawScale, then update correctionGroup and bg imperatively.
	useFrame(state=>{
		const grp = correctionGroupRef.current;
		const dot = dotRef.current;
		const bg = bgRef.current;
		if (!grp) return;

		const mtp = calcMeterToPixelRatio(state.camera as THREE.OrthographicCamera, state.size.width);
		const {naturalW, naturalH, paddedW, paddedH, rawScale, cx, cy} = naturalRef.current;
		if (naturalW <= 0 || naturalH <= 0) return;

		// Start from the natural (unconstrained) scale.
		let scale = rawScale;

		// Clamp by pixel width/height using the padded dimensions (padding is baked into the scale target).
		if (sizeInfo.minPixelWidth > 0) scale = Math.max(scale, sizeInfo.minPixelWidth / (paddedW * mtp));
		if (sizeInfo.maxPixelWidth > 0) scale = Math.min(scale, sizeInfo.maxPixelWidth / (paddedW * mtp));
		// don't be too fancy; if min-height is set to 10px, and text has two lines, just have block's min-height be 20px
		const paddedH_for1Line = paddedH / lineCount;
		if (sizeInfo.minPixelHeight > 0) scale = Math.max(scale, sizeInfo.minPixelHeight / (paddedH_for1Line * mtp));
		if (sizeInfo.maxPixelHeight > 0) scale = Math.min(scale, sizeInfo.maxPixelHeight / (paddedH_for1Line * mtp));

		grp.scale.setScalar(scale);

		// Background size = text + padding (padding scaled with everything else).
		const bgW = paddedW * scale;
		const bgH = paddedH * scale;
		const expectedCx = anchorX === "center" ? 0 : anchorX === "left" ? bgW / 2 : -bgW / 2;
		const expectedCy = anchorY === "middle" ? 0 : anchorY === "top" ? -bgH / 2 : bgH / 2;
		grp.position.set(expectedCx - cx * scale, expectedCy - cy * scale, 0);

		if (bg) {
			bg.scale.set(bgW, bgH, 1);
			bg.position.set(expectedCx, expectedCy, -0.001);
			SetRoundedRectSize(bgMaterial, bgW, bgH);
			const finalLineHeight = (naturalH / lineCount) * scale;
			SetRoundedRectRadius(bgMaterial, finalLineHeight * borderRadiusPercent);
		}

		// Dot: at most 10px, unaffected by correctionGroup
		if (dot) {
			// if label has a min width/height (in px), object itself may be too small to see
			// ...so ensure the dot is at least 1px in size (so viewer can discern true location of named object)
			const minDotSize_px = sizeInfo.minPixelWidth > 0 || sizeInfo.minPixelHeight > 0 ? 1 : 0;
			const dotSizeWorld = (initialFontSize * 0.1).KeepBetween(minDotSize_px / mtp, 10 / mtp);
			dot.scale.set(dotSizeWorld, dotSizeWorld, 1);
		}
	});

	return (
		<group position={finalPos}>
			{/* Background lives in the outer group — sized imperatively in onSync via rawScale, unaffected by text padding */}
			<mesh ref={bgRef} material={bgMaterial} renderOrder={renderOrders.labelBackground}>
				<planeGeometry args={[1, 1]}/>
			</mesh>
			{/* correctionGroup applies the uniform scale + centering offset for the text */}
			<group ref={correctionGroupRef}>
				<Text ref={textRef} font="/Fonts/quicksand-v30-latin_latin-ext_vietnamese-500.woff"
					anchorX={anchorX} anchorY={anchorY} textAlign="center" fontSize={initialFontSize}
					color="white" material-toneMapped={false} onSync={onSync} renderOrder={renderOrders.labelText}
				>{text}</Text>
			</group>
			{/* Dot lives in the outer group so correctionGroup scale/position doesn't affect it */}
			{placement !== LabelPlacement.center &&
				<mesh ref={dotRef} position={[0, 0, 0.001]} renderOrder={renderOrders.labelDot}>
					<planeGeometry args={[1, 1]}/>
					<meshBasicMaterial color="red" depthTest={false}
						transparent={true} // needed to (reliably?) show above terrain proc-mats
					/>
				</mesh>
			}
		</group>
	);
};